/**
 * Workflow History Panel Component
 * Task 1: Display workflow logs and allow retry of failed triggers
 * Shows all automatic trigger attempts for a deal with status and timestamps
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { retryFailedTrigger } from '../services/workflowTriggers';
import './WorkflowHistoryPanel.css';
import {
  WORKFLOW_LOG_STATUS_LABEL,
  workflowLogStatus,
  type WorkflowLogStatus,
} from '../lib/workflowLogStatus';

interface WorkflowLog {
  id: string;
  deal_id: string;
  trigger_name: string;
  /* Whatever word a writer used. Read it through workflowLogStatus, never directly. */
  trigger_status?: string | null;
  success: boolean;
  error_message?: string;
  details?: Record<string, any>;
  executed_at: string;
  retried_at?: string;
  retried_by_user_id?: string;
}

interface WorkflowHistoryPanelProps {
  dealId: string;
  showTitle?: boolean;
}

export const WorkflowHistoryPanel: React.FC<WorkflowHistoryPanelProps> = ({
  dealId,
  showTitle = true
}) => {
  const [logs, setLogs] = useState<WorkflowLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);

  useEffect(() => {
    loadWorkflowLogs();
  }, [dealId]);

  const loadWorkflowLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: err } = await supabase
        .from('workflow_logs')
        .select('*')
        .eq('deal_id', dealId)
        .order('executed_at', { ascending: false });

      if (err) throw err;

      setLogs(data || []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load workflow logs';
      setError(msg);
      console.error('Workflow history load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (logId: string) => {
    try {
      setRetrying(logId);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const result = await retryFailedTrigger(logId, user.id);

      if (result.success) {
        // Reload logs
        await loadWorkflowLogs();
      } else {
        setError(result.message);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Retry failed';
      setError(msg);
    } finally {
      setRetrying(null);
    }
  };

  /*
   * Three lists, and every row is in exactly one. The pair of filters that
   * stood here matched two literal words and dropped everything else on the
   * floor: 94 of the 280 rows on staging, a bounced email among them.
   */
  const statusOf = (log: WorkflowLog): WorkflowLogStatus => workflowLogStatus(log);
  const failedLogs = logs.filter(log => statusOf(log) === 'failed');
  const skippedLogs = logs.filter(log => statusOf(log) === 'skipped');
  const successLogs = logs.filter(log => statusOf(log) === 'success');

  if (loading) {
    return <div className="workflow-history loading">Loading workflow history...</div>;
  }

  if (!logs || logs.length === 0) {
    return (
      <div className="workflow-history empty">
        <p>No workflow events recorded for this deal</p>
      </div>
    );
  }

  return (
    <div className="workflow-history-panel">
      {showTitle && (
        <div className="panel-header">
          <h2>Workflow History</h2>
          {failedLogs.length > 0 && (
            <div className="failed-badge">{failedLogs.length} Failed</div>
          )}
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      {/* Failed Triggers Section */}
      {failedLogs.length > 0 && (
        <div className="workflow-section failed">
          <h3>Failed Triggers (Needs Attention)</h3>
          <div className="log-list">
            {failedLogs.map(log => (
              <WorkflowLogItem
                key={log.id}
                log={log}
                onRetry={() => handleRetry(log.id)}
                isRetrying={retrying === log.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Steps that did not apply here. Nothing to retry, nothing to fix. */}
      {skippedLogs.length > 0 && (
        <div className="workflow-section skipped">
          <h3>Did Not Apply</h3>
          <div className="log-list">
            {skippedLogs.map(log => (
              <WorkflowLogItem
                key={log.id}
                log={log}
              />
            ))}
          </div>
        </div>
      )}

      {/* Success Triggers Section */}
      {successLogs.length > 0 && (
        <div className="workflow-section success">
          <h3>Completed Triggers</h3>
          <div className="log-list">
            {successLogs.map(log => (
              <WorkflowLogItem
                key={log.id}
                log={log}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Individual Workflow Log Item Component
 */
interface WorkflowLogItemProps {
  log: WorkflowLog;
  onRetry?: () => void;
  isRetrying?: boolean;
}

const WorkflowLogItem: React.FC<WorkflowLogItemProps> = ({
  log,
  onRetry,
  isRetrying = false
}) => {
  const [expanded, setExpanded] = useState(false);

  const triggerLabel = getTriggerLabel(log.trigger_name);
  const status = workflowLogStatus(log);
  const statusIcon = status === 'success' ? '✓' : status === 'skipped' ? '–' : '✕';
  const statusClass = status;

  return (
    <div className={`log-item ${statusClass}`}>
      <div className="log-header" onClick={() => setExpanded(!expanded)}>
        <div className="log-status">
          <span className={`status-icon ${statusClass}`}>{statusIcon}</span>
          <span className="status-text">{WORKFLOW_LOG_STATUS_LABEL[status]}</span>
        </div>

        <div className="log-trigger">
          <span className="trigger-name">{triggerLabel}</span>
          <span className="trigger-time">
            {new Date(log.executed_at).toLocaleString('en-AU', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        </div>

        <div className="log-actions">
          {log.success && !log.retried_at && (
            <span className="original">Original</span>
          )}
          {log.retried_at && (
            <span className="retried">Retried</span>
          )}
          {status === 'failed' && onRetry && (
            <button
              className="btn-retry"
              onClick={e => {
                e.stopPropagation();
                onRetry();
              }}
              disabled={isRetrying}
            >
              {isRetrying ? 'Retrying...' : 'Retry'}
            </button>
          )}
        </div>

        <button className="expand-btn" onClick={() => setExpanded(!expanded)}>
          {expanded ? '−' : '+'}
        </button>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="log-details">
          {log.error_message && (
            <div className={`error-box ${status === 'skipped' ? 'why-box' : ''}`}>
              <strong>{status === 'skipped' ? 'Why:' : 'Error:'}</strong>
              <p>{log.error_message}</p>
            </div>
          )}

          {log.details && Object.keys(log.details).length > 0 && (
            <div className="details-box">
              <strong>Details:</strong>
              <pre>{JSON.stringify(log.details, null, 2)}</pre>
            </div>
          )}

          {log.retried_at && (
            <div className="retry-info">
              <strong>Retry History:</strong>
              <p>Retried at {new Date(log.retried_at).toLocaleString('en-AU')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Get human-readable label for trigger name
 */
const getTriggerLabel = (triggerName: string): string => {
  const labels: Record<string, string> = {
    'BOS_SMS_SENT': 'Vehicle Purchase Agreement SMS',
    'DD_COLLECTION_INITIATED': 'DD Collection Form',
    'INSPECTION_BOOKING_INITIATED': 'Inspection Booking',
    'PPSR_DISCHARGE_INITIATED': 'PPSR Discharge',
    'INVOICE_GENERATED': 'Invoice Generation',
    'XERO_SYNC_INITIATED': 'Xero Sync',
    'MANUAL_PAYMENT_OVERRIDE': 'Manual Payment Override'
  };

  return labels[triggerName] || triggerName;
};

export default WorkflowHistoryPanel;
